import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isFreeUser } from './utils/planAccess';

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("annual");
  const planStatus = isFreeUser() ? "Basic" : "Pro";
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (planType) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription/create-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planType }),
      });
  
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setLoading(false);
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setLoading(false);
      alert('Error starting checkout session.');
    }
  };
  
  const handleContact = () => {
    window.open("https://proactr.com/contact", "_blank");
  };

  return (
    <main className="w-full bg-gray-50 py-20 px-6 text-center">
      <h1 className="text-5xl font-bold text-purple-800">Choose Your Plan</h1>
      <p className="mt-4 text-lg text-secondary-navy max-w-3xl mx-auto">
        Start with a <strong>free plan</strong> or upgrade to unlock more features.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">

        {/* Free Plan */}
        <div className="flex flex-col justify-between p-8 bg-white rounded-xl shadow-xl border border-gray-300">
          <h2 className="text-3xl font-bold text-green-600 mt-[25px]">Basic</h2>
          <p className="mt-2 text-2xl font-semibold text-secondary-navy">Free</p>
          <p className="mt-2 text-sm text-gray-500">Get started for free. No credit card required.</p>
          <ul className="mt-6 space-y-2 text-secondary-navy text-left">
            <li>✅ AI-Driven Role-Play</li>
            <li>✅ Practice with pre-written scenarios</li>
            <li>✅ Create up to 3 custom scenarios</li>
            <li>✅ Motivation score feedback only</li>
            <li>🚫 Edit custom scenarios</li>
            <li>🚫 Full analytics or reports</li>
          </ul>
          <button
            className="mt-6 px-6 py-3 text-lg font-semibold bg-gray-300 text-white rounded-lg cursor-not-allowed"
            disabled
          >
            {planStatus === "Basic" ? "This is your plan" : "Already Upgraded"}
          </button>
        </div>

        {/* Pro Plan */}
        <div className="p-8 bg-white rounded-xl shadow-xl border-4 border-purple-800 transform scale-[1.03] relative">
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
            <div className="flex rounded-full overflow-hidden shadow-md border border-purple-800">
              {["annual", "monthly"].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={`px-6 py-2 text-sm font-semibold transition-colors duration-200 ${
                    billingCycle === cycle
                      ? "bg-purple-800 text-white"
                      : "bg-gray-100 text-purple-800 hover:bg-gray-200"
                  }`}
                >
                  {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="relative mt-6">
            <h2 className="text-3xl font-bold text-purple-800 text-center">Pro</h2>
            {billingCycle === "annual" && (
              <span className="absolute right-1 top-0 transform -translate-y-1/2 px-2 py-1 text-xs font-bold text-white bg-green-600 rounded-full">
                Best Value
              </span>
            )}
          </div>
          <p className="mt-2 text-2xl font-semibold text-secondary-navy">
            {billingCycle === "annual" ? "$25/month" : "$30/month"}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {billingCycle === "annual" ? "Billed annually at $295 (save 18%)" : "Billed monthly, cancel anytime"}
          </p>
          <ul className="mt-6 space-y-2 text-secondary-navy text-left">
            <li><strong>Everything in Basic, plus:</strong></li>
            <li>✅ Unlimited access to all scenarios</li>
            <li>✅ Unlimited custom scenario creation</li>
            <li>✅ Unlimited custom scenario editing</li>
            <li>✅ Full reports across 12 dimensions</li>
            <li>✅ Detailed analytics and session history</li>
            <li>✅ Personality-based feedback</li>
            <li>✅ Personalized learning suggestions</li>
          </ul>

          {planStatus === "Pro" ? (
            <button
              className="mt-6 w-full px-6 py-3 text-lg font-semibold bg-gray-300 text-white rounded-lg cursor-not-allowed"
              disabled
            >
              This is your plan
            </button>
          ) : (
            <button
              className="mt-6 w-full px-6 py-3 text-lg font-semibold bg-purple-800 text-white rounded-lg hover:bg-green-600 transition"
              onClick={() => handleSubscribe(billingCycle)}
            >
              Upgrade Now
            </button>
          )}
        </div>

        {/* Enterprise Plan */}
        <div className="flex flex-col justify-between p-8 bg-white rounded-xl shadow-xl border border-gray-300">
          <h2 className="text-3xl font-bold text-tertiary-red mt-[25px]">Enterprise</h2>
          <p className="mt-2 text-2xl font-semibold text-secondary-navy">Contact Us</p>
          <p className="mt-2 text-sm text-gray-500">For organizations and advanced teams</p>
          <ul className="mt-6 space-y-2 text-secondary-navy text-left">
            <li><strong>Everything in Pro, plus:</strong></li>
            <li>✅ Admin portal and group licensing</li>
            <li>✅ Team progress reports and analytics</li>
            <li>✅ Dedicated onboarding support</li>
            <li>✅ Optional SSO & enhanced security</li>
            <li>✅ Integration and customization support</li>
          </ul>
          <button
            className="mt-6 px-6 py-3 text-lg font-semibold bg-red-400 text-white rounded-lg hover:bg-purple-800 transition"
            onClick={handleContact}
          >
            Contact Sales
          </button>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <p className="ml-4 text-white text-lg">Preparing checkout...</p>
        </div>
        )}

    </main>
  );
};

export default PricingPage;
